/**
 * Global mobile bottom navigation — active states and drawer triggers.
 */

class MobileBottomNav {
  constructor() {
    this.bottomNav = document.querySelector('[data-mobile-bottom-nav]');
    this.navItems = this.bottomNav?.querySelectorAll('[data-nav-item]') ?? [];
    this.menuToggle = this.bottomNav?.querySelector('[data-mobile-menu-toggle]');
    this.searchModal = document.getElementById('search-modal');
    this.cartDrawer = document.querySelector('cart-drawer-component');
    this.isHomeTemplate = document.querySelector('main[data-template]')?.dataset.template === 'index';
    this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
    this.resizeObserver = null;

    if (!this.bottomNav) return;

    this.bindMenuId();
    this.bindNavItems();
    this.bindTriggers();
    this.bindOverlayStates();
    this.setupBodyOffset();
    this.updateActiveState();
  }

  setupBodyOffset() {
    this.updateBodyOffset();

    this.mobileMediaQuery.addEventListener('change', () => {
      this.updateBodyOffset();
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateBodyOffset();
      });
      this.resizeObserver.observe(this.bottomNav);
    } else {
      window.addEventListener('resize', () => this.updateBodyOffset());
    }

    window.addEventListener('orientationchange', () => {
      requestAnimationFrame(() => this.updateBodyOffset());
    });
  }

  updateBodyOffset() {
    if (!this.bottomNav) return;

    if (!this.mobileMediaQuery.matches) {
      document.documentElement.style.removeProperty('--mobile-bottom-nav-height');
      return;
    }

    const height = Math.ceil(this.bottomNav.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--mobile-bottom-nav-height', `${height}px`);
  }

  bindMenuId() {
    const menu = document.querySelector('[data-mobile-menu]');
    if (this.menuToggle && menu?.id) {
      this.menuToggle.setAttribute('aria-controls', menu.id);
    }
  }

  bindNavItems() {
    for (const item of this.navItems) {
      item.addEventListener('click', () => {
        const navKey = item.dataset.navItem;
        if (navKey) {
          this.setActiveNavItem(navKey);
        }
      });
    }
  }

  bindTriggers() {
    this.menuToggle?.addEventListener('click', () => {
      window.servicesDrawer?.close();
      document.dispatchEvent(new CustomEvent('mobile-menu:toggle'));
    });

    this.bottomNav.querySelector('[data-search-modal-trigger]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.openSearch();
    });

    this.bottomNav.querySelector('[data-cart-drawer-trigger]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.openCartDrawer();
    });

    this.bottomNav.querySelector('[data-account-trigger]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.openAccount();
    });

    this.bottomNav.querySelector('[data-services-drawer-trigger]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.openServicesDrawer();
    });
  }

  bindOverlayStates() {
    const refresh = () => this.updateActiveState();

    this.searchModal?.addEventListener('dialog:open', refresh);
    this.searchModal?.addEventListener('dialog:close', refresh);
    this.cartDrawer?.addEventListener('dialog:open', refresh);
    this.cartDrawer?.addEventListener('dialog:close', refresh);
    document.addEventListener('mobile-menu:opened', refresh);
    document.addEventListener('mobile-menu:closed', refresh);
    document.addEventListener('services-drawer:opened', refresh);
    document.addEventListener('services-drawer:closed', refresh);
  }

  /** @param {string} [navKey] */
  setActiveNavItem(navKey) {
    for (const navItem of this.navItems) {
      navItem.classList.remove('is-active');
    }

    if (!navKey) return;

    this.bottomNav.querySelector(`[data-nav-item="${navKey}"]`)?.classList.add('is-active');
    requestAnimationFrame(() => this.updateBodyOffset());
  }

  isMenuOpen() {
    return document.querySelector('[data-mobile-menu]')?.classList.contains('is-active') ?? false;
  }

  isSearchOpen() {
    const dialog = this.searchModal?.querySelector('dialog');
    return Boolean(dialog?.open);
  }

  isCartOpen() {
    const dialog = this.cartDrawer?.querySelector('dialog');
    return Boolean(dialog?.open);
  }

  isServicesDrawerOpen() {
    return window.servicesDrawer?.isOpen ?? false;
  }

  updateActiveState() {
    if (this.isMenuOpen()) {
      this.setActiveNavItem('menu');
      return;
    }

    if (this.isSearchOpen()) {
      this.setActiveNavItem('search');
      return;
    }

    if (this.isCartOpen()) {
      this.setActiveNavItem('cart');
      return;
    }

    if (this.isServicesDrawerOpen()) {
      this.setActiveNavItem('services');
      return;
    }

    if (this.isHomeTemplate) {
      this.setActiveNavItem('home');
      return;
    }

    this.setActiveNavItem();
  }

  openSearch() {
    window.servicesDrawer?.close();
    const wasMenuOpen = this.isMenuOpen();
    const openSearchModal = () => {
      const searchBarInput = document.querySelector('[data-search-bar-input-mobile]');
      if (searchBarInput) {
        searchBarInput.focus();
        searchBarInput.click();
        if (wasMenuOpen) {
          setTimeout(() => searchBarInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 300);
        }
        return;
      }

      document.querySelector('search-button button')?.click();
    };

    if (wasMenuOpen) {
      document.addEventListener('mobile-menu:closed', openSearchModal, { once: true });
      document.dispatchEvent(new CustomEvent('mobile-menu:close'));
    } else {
      openSearchModal();
    }
  }

  openCartDrawer() {
    if (!this.cartDrawer || typeof this.cartDrawer.open !== 'function') return;

    window.servicesDrawer?.close();
    const wasMenuOpen = this.isMenuOpen();
    if (wasMenuOpen) {
      document.dispatchEvent(new CustomEvent('mobile-menu:close'));
      setTimeout(() => this.cartDrawer.open(), 300);
    } else {
      this.cartDrawer.open();
    }
  }

  openServicesDrawer() {
    const wasMenuOpen = this.isMenuOpen();
    const open = () => window.servicesDrawer?.open();

    if (wasMenuOpen) {
      document.addEventListener('mobile-menu:closed', open, { once: true });
      document.dispatchEvent(new CustomEvent('mobile-menu:close'));
    } else {
      open();
    }
  }

  openAccount() {
    const shopifyAccount = document.querySelector('shopify-account');
    if (!shopifyAccount) return;

    window.servicesDrawer?.close();
    const wasMenuOpen = this.isMenuOpen();
    if (wasMenuOpen) {
      document.dispatchEvent(new CustomEvent('mobile-menu:close'));
    }

    setTimeout(() => {
      const triggerButton = shopifyAccount.shadowRoot?.querySelector('button[aria-haspopup="dialog"]')
        || shopifyAccount.querySelector('button');
      triggerButton?.click();
    }, wasMenuOpen ? 300 : 0);
  }

  setInert(inert) {
    if (this.bottomNav) {
      this.bottomNav.inert = inert;
    }
  }

  getFocusableElements() {
    if (!this.bottomNav || this.bottomNav.inert) return [];

    return Array.from(
      this.bottomNav.querySelectorAll('button:not([disabled]), a[href]')
    ).filter(el => {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' &&
             style.visibility !== 'hidden' &&
             el.offsetParent !== null;
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.mobileBottomNav = new MobileBottomNav();
  });
} else {
  window.mobileBottomNav = new MobileBottomNav();
}
